/* eslint-disable react-refresh/only-export-components */
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

export type Locale = 'tr' | 'en';

interface I18nContextValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
  tt: (tr: string, en: string) => string;
}

const STORAGE_KEY = 'uiLocaleV1';

const I18nContext = createContext<I18nContextValue | null>(null);

function readInitialLocale(): Locale {
  if (typeof window === 'undefined') {
    return 'tr';
  }

  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === 'tr' || saved === 'en') {
    return saved;
  }
  return 'tr';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(readInitialLocale);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, locale);
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      tt: (tr: string, en: string) => (locale === 'tr' ? tr : en),
    }),
    [locale]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
}
